import type { TableName } from '../db/schema-table-list';

export const PermissionsDefinitions = {
  create: 'create',
  read: 'read',
  update: 'update',
  delete: 'delete'
} as const;

export type PermissionLevels = 'create' | 'read' | 'update' | 'delete';
export type Roles = 'all' | 'owner' | 'editor' | 'viewer';

const ORGANIZATION_USER_ROLE_KEYS = [
  'admin',
  'gm',
  'hr',
  'mgr',
  'employee',
  'employee_lead',
  'owner'
] as const;

export type OrganizationUserRole = (typeof ORGANIZATION_USER_ROLE_KEYS)[number];

export const ORGANIZATION_USER_ROLES: OrganizationUserRole[] = [
  ...ORGANIZATION_USER_ROLE_KEYS
];

export const DEFAULT_ORGANIZATION_USER_ROLE: OrganizationUserRole = 'employee';

const ALL_PERMISSIONS = [
  ...Object.values(PermissionsDefinitions)
] as PermissionLevels[];

export const RolePermissionLevels: Record<Roles, PermissionLevels[]> = {
  all: ALL_PERMISSIONS,
  owner: ALL_PERMISSIONS,
  editor: [
    PermissionsDefinitions.read,
    PermissionsDefinitions.update
  ] as PermissionLevels[],
  viewer: [PermissionsDefinitions.read] as PermissionLevels[]
};

export type EntityPermissions = Record<TableName, PermissionLevels[]>;

const CHAT_STATE_PERMISSIONS = {
  chat_channel_members: RolePermissionLevels.all,
  chat_channel_reads: RolePermissionLevels.all
} as const;

// Every organization role can send chat attachments. Mutation services add
// the stricter parent-specific rules: only menu/recipe managers may create or
// remove those files, and sent chat files are immutable.
const CHAT_FILE_PERMISSIONS: PermissionLevels[] = [
  PermissionsDefinitions.create,
  PermissionsDefinitions.read
];

// Agent identity/execution tables (Phase B of
// docs/plans/2026-09-17-agentic-rag-and-conversations.md) use their own
// narrow Predicate-based privacy (see agent-profile.ts, agent-run.ts,
// agent-run-step.ts, agent-evidence.ts) rather than this role matrix --
// same pattern as `knowledge_articles`/`organization_search_preferences`
// below. Every role still needs an entry because `EntityPermissions` is a
// full `Record<TableName, ...>`.
const AGENT_TABLE_PERMISSIONS: {
  agent_profiles: PermissionLevels[];
  agent_runs: PermissionLevels[];
  agent_run_steps: PermissionLevels[];
  agent_evidence: PermissionLevels[];
} = {
  agent_profiles: [],
  agent_runs: [],
  agent_run_steps: [],
  agent_evidence: []
};

export const EmployeeEntityPermissions: Record<
  OrganizationUserRole,
  EntityPermissions
> = {
  admin: {
    ...CHAT_STATE_PERMISSIONS,
    ...AGENT_TABLE_PERMISSIONS,
    announcements: RolePermissionLevels.all,
    categories: RolePermissionLevels.all,
    chat_channels: RolePermissionLevels.all,
    chat_messages: RolePermissionLevels.all,
    edges: RolePermissionLevels.all,
    files: RolePermissionLevels.all,
    images: RolePermissionLevels.all,
    ingredients: RolePermissionLevels.all,
    inventory_items: RolePermissionLevels.all,
    knowledge_articles: [],
    manager_logbook_entries: RolePermissionLevels.all,
    menus: RolePermissionLevels.all,
    menu_imports: RolePermissionLevels.all,
    heuristic_rules: RolePermissionLevels.all,
    flavor_nodes: RolePermissionLevels.all,
    monetary_values: RolePermissionLevels.all,
    organization_search_preferences: [],
    user_contacts: [],
    organization_users: RolePermissionLevels.all,
    organizations: RolePermissionLevels.all,
    permissions: RolePermissionLevels.all,
    properties: RolePermissionLevels.all,
    recipe_ingredients: RolePermissionLevels.all,
    recipes: RolePermissionLevels.all,
    sources: RolePermissionLevels.all,
    unavailable_items: RolePermissionLevels.all,
    users: RolePermissionLevels.all,
    wares: RolePermissionLevels.all
  },
  owner: {
    ...CHAT_STATE_PERMISSIONS,
    ...AGENT_TABLE_PERMISSIONS,
    announcements: RolePermissionLevels.all,
    categories: RolePermissionLevels.all,
    chat_channels: RolePermissionLevels.all,
    chat_messages: RolePermissionLevels.all,
    edges: RolePermissionLevels.all,
    files: RolePermissionLevels.all,
    images: RolePermissionLevels.all,
    ingredients: RolePermissionLevels.all,
    inventory_items: RolePermissionLevels.all,
    knowledge_articles: [],
    manager_logbook_entries: RolePermissionLevels.all,
    menus: RolePermissionLevels.all,
    menu_imports: RolePermissionLevels.all,
    heuristic_rules: RolePermissionLevels.all,
    flavor_nodes: RolePermissionLevels.all,
    monetary_values: RolePermissionLevels.all,
    organization_search_preferences: [],
    user_contacts: [],
    organization_users: RolePermissionLevels.owner,
    organizations: RolePermissionLevels.owner,
    permissions: RolePermissionLevels.all,
    properties: RolePermissionLevels.all,
    recipe_ingredients: RolePermissionLevels.all,
    recipes: RolePermissionLevels.all,
    sources: RolePermissionLevels.all,
    unavailable_items: RolePermissionLevels.all,
    users: RolePermissionLevels.all,
    wares: RolePermissionLevels.all
  },
  gm: {
    ...CHAT_STATE_PERMISSIONS,
    ...AGENT_TABLE_PERMISSIONS,
    announcements: RolePermissionLevels.all,
    categories: RolePermissionLevels.all,
    chat_channels: RolePermissionLevels.all,
    chat_messages: RolePermissionLevels.all,
    edges: RolePermissionLevels.all,
    files: RolePermissionLevels.all,
    images: RolePermissionLevels.all,
    ingredients: RolePermissionLevels.all,
    inventory_items: RolePermissionLevels.all,
    knowledge_articles: [],
    manager_logbook_entries: RolePermissionLevels.all,
    menus: RolePermissionLevels.all,
    menu_imports: RolePermissionLevels.all,
    heuristic_rules: RolePermissionLevels.all,
    flavor_nodes: RolePermissionLevels.viewer,
    monetary_values: RolePermissionLevels.all,
    organization_search_preferences: [],
    user_contacts: [],
    organization_users: RolePermissionLevels.owner,
    organizations: RolePermissionLevels.owner,
    permissions: RolePermissionLevels.all,
    properties: RolePermissionLevels.all,
    recipe_ingredients: RolePermissionLevels.all,
    recipes: RolePermissionLevels.all,
    sources: RolePermissionLevels.all,
    unavailable_items: RolePermissionLevels.all,
    users: RolePermissionLevels.all,
    wares: RolePermissionLevels.all
  },
  mgr: {
    ...CHAT_STATE_PERMISSIONS,
    ...AGENT_TABLE_PERMISSIONS,
    // Authoring announcements is a mgr+ action (mgr/hr/gm/owner/admin), so
    // unlike this role's usual `editor` (read+update) shape elsewhere, mgr
    // gets full announcement permissions including create/delete.
    announcements: RolePermissionLevels.all,
    categories: RolePermissionLevels.editor,
    chat_channels: RolePermissionLevels.all,
    chat_messages: RolePermissionLevels.all,
    edges: RolePermissionLevels.all,
    files: RolePermissionLevels.all,
    images: RolePermissionLevels.editor,
    ingredients: RolePermissionLevels.editor,
    inventory_items: RolePermissionLevels.editor,
    knowledge_articles: [],
    manager_logbook_entries: RolePermissionLevels.all,
    menus: RolePermissionLevels.all,
    menu_imports: RolePermissionLevels.all,
    heuristic_rules: RolePermissionLevels.viewer,
    flavor_nodes: RolePermissionLevels.viewer,
    monetary_values: RolePermissionLevels.all,
    organization_search_preferences: [],
    user_contacts: [],
    organization_users: RolePermissionLevels.all,
    organizations: RolePermissionLevels.viewer,
    permissions: RolePermissionLevels.editor,
    properties: RolePermissionLevels.editor,
    recipe_ingredients: RolePermissionLevels.all,
    recipes: RolePermissionLevels.all,
    sources: RolePermissionLevels.editor,
    unavailable_items: RolePermissionLevels.all,
    users: RolePermissionLevels.editor,
    wares: RolePermissionLevels.editor
  },
  hr: {
    ...CHAT_STATE_PERMISSIONS,
    ...AGENT_TABLE_PERMISSIONS,
    // See mgr's announcements entry above -- same mgr+ authoring rationale.
    announcements: RolePermissionLevels.all,
    categories: RolePermissionLevels.viewer,
    chat_channels: RolePermissionLevels.all,
    chat_messages: RolePermissionLevels.all,
    edges: RolePermissionLevels.all,
    files: CHAT_FILE_PERMISSIONS,
    images: RolePermissionLevels.viewer,
    ingredients: RolePermissionLevels.viewer,
    inventory_items: RolePermissionLevels.viewer,
    knowledge_articles: [],
    manager_logbook_entries: RolePermissionLevels.all,
    menus: RolePermissionLevels.viewer,
    menu_imports: [],
    heuristic_rules: RolePermissionLevels.viewer,
    flavor_nodes: RolePermissionLevels.viewer,
    monetary_values: RolePermissionLevels.viewer,
    organization_search_preferences: [],
    user_contacts: [],
    organization_users: RolePermissionLevels.all,
    organizations: RolePermissionLevels.viewer,
    permissions: RolePermissionLevels.viewer,
    properties: RolePermissionLevels.viewer,
    recipe_ingredients: RolePermissionLevels.viewer,
    recipes: RolePermissionLevels.viewer,
    sources: RolePermissionLevels.viewer,
    unavailable_items: RolePermissionLevels.all,
    users: RolePermissionLevels.editor,
    wares: RolePermissionLevels.viewer
  },
  employee: {
    ...CHAT_STATE_PERMISSIONS,
    ...AGENT_TABLE_PERMISSIONS,
    announcements: RolePermissionLevels.viewer,
    categories: RolePermissionLevels.viewer,
    chat_channels: RolePermissionLevels.all,
    chat_messages: RolePermissionLevels.all,
    edges: RolePermissionLevels.all,
    files: CHAT_FILE_PERMISSIONS,
    images: RolePermissionLevels.viewer,
    ingredients: RolePermissionLevels.viewer,
    inventory_items: RolePermissionLevels.viewer,
    knowledge_articles: [],
    manager_logbook_entries: RolePermissionLevels.viewer,
    menus: RolePermissionLevels.viewer,
    menu_imports: [],
    heuristic_rules: RolePermissionLevels.viewer,
    flavor_nodes: RolePermissionLevels.viewer,
    monetary_values: RolePermissionLevels.viewer,
    organization_search_preferences: [],
    user_contacts: [],
    organization_users: RolePermissionLevels.viewer,
    organizations: RolePermissionLevels.viewer,
    permissions: RolePermissionLevels.viewer,
    properties: RolePermissionLevels.viewer,
    recipe_ingredients: RolePermissionLevels.viewer,
    recipes: RolePermissionLevels.viewer,
    sources: RolePermissionLevels.viewer,
    // 86-ing an item is a frontline task -- every employee can add and
    // resolve unavailable-item rows, not just mgr+ (unlike most tables in
    // this matrix, which cap employee/employee_lead at viewer).
    unavailable_items: RolePermissionLevels.all,
    users: RolePermissionLevels.viewer,
    wares: RolePermissionLevels.viewer
  },
  employee_lead: {
    ...CHAT_STATE_PERMISSIONS,
    ...AGENT_TABLE_PERMISSIONS,
    announcements: RolePermissionLevels.viewer,
    categories: RolePermissionLevels.viewer,
    chat_channels: RolePermissionLevels.all,
    chat_messages: RolePermissionLevels.all,
    edges: RolePermissionLevels.all,
    files: CHAT_FILE_PERMISSIONS,
    images: RolePermissionLevels.viewer,
    ingredients: RolePermissionLevels.viewer,
    inventory_items: RolePermissionLevels.viewer,
    knowledge_articles: [],
    manager_logbook_entries: RolePermissionLevels.viewer,
    menus: RolePermissionLevels.viewer,
    menu_imports: [],
    heuristic_rules: RolePermissionLevels.viewer,
    flavor_nodes: RolePermissionLevels.viewer,
    monetary_values: RolePermissionLevels.viewer,
    organization_search_preferences: [],
    user_contacts: [],
    organization_users: RolePermissionLevels.viewer,
    organizations: RolePermissionLevels.viewer,
    permissions: RolePermissionLevels.viewer,
    properties: RolePermissionLevels.viewer,
    recipe_ingredients: RolePermissionLevels.viewer,
    recipes: RolePermissionLevels.viewer,
    sources: RolePermissionLevels.viewer,
    unavailable_items: RolePermissionLevels.all,
    users: RolePermissionLevels.viewer,
    wares: RolePermissionLevels.viewer
  }
};
